import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { discrepancyReviewService } from '../services/discrepancyReviewService';
import { stockConfigService } from '@/services/stockConfigService';
import type { LocationApiResponse } from '@/services/stockConfigService';
import type { DiscrepancyItem, DiscrepancyFilters, DiscrepancySummaryData } from '@/types/discrepancy-review.types';

interface Location {
  id: string;
  name: string;
}

interface Category {
  id: number;
  name: string;
}

const getDefaultFilters = (): DiscrepancyFilters => {
  const today = new Date();
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  return {
    locationId: '',
    shift: 'all',
    type: 'all',
    categoryId: '',
    dateFrom: weekAgo.toISOString().split('T')[0],
    dateTo: today.toISOString().split('T')[0],
    onlyDiscrepancies: false,
  };
};

export const useDiscrepancyReview = () => {
  const [items, setItems] = useState<DiscrepancyItem[]>([]);
  const [summary, setSummary] = useState<DiscrepancySummaryData>({ total: 0, withDiscrepancy: 0, transfers: 0, purchaseOrders: 0 });
  const [filters, setFilters] = useState<DiscrepancyFilters>(getDefaultFilters);
  const [editedQuantities, setEditedQuantities] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Combo data
  const [locations, setLocations] = useState<Location[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const loadComboData = async () => {
      try {
        const [locResponse, catResponse] = await Promise.all([
          stockConfigService.getLocations(),
          stockConfigService.getCategories(),
        ]);
        const mapped = (locResponse as LocationApiResponse).data.map(loc => ({ id: loc.id, name: loc.nombre }));
        setLocations(mapped);
        if (mapped.length > 0) {
          setFilters(prev => ({ ...prev, locationId: String(mapped[0].id) }));
        }
        if (catResponse.success) setCategories(catResponse.data);
      } catch (error) {
        console.error('Error loading combo data:', error);
      }
    };
    loadComboData();
  }, []);

  const updateFilters = useCallback((partial: Partial<DiscrepancyFilters>) => {
    setFilters(prev => ({ ...prev, ...partial }));
  }, []);

  const fetchDiscrepancies = useCallback(async () => {
    if (!filters.locationId) {
      toast.error('Selecciona un local');
      return;
    }
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await discrepancyReviewService.getDiscrepancies(filters);
      if (response.success) {
        setItems(response.data);
        setSummary(response.meta);
        setEditedQuantities({});
      }
    } catch (error) {
      console.error('Error fetching discrepancies:', error);
      toast.error('Error al buscar diferencias');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const updateConfirmedQuantity = useCallback((itemId: string, quantity: number) => {
    setEditedQuantities(prev => ({ ...prev, [itemId]: quantity }));
  }, []);

  const resetEdits = useCallback(() => {
    setEditedQuantities({});
  }, []);

  const applyAdjustments = useCallback(async () => {
    const adjustments = Object.entries(editedQuantities)
      .map(([id, confirmedQuantity]) => {
        const item = items.find(i => i.id === id);
        if (!item) return null;
        const delta = confirmedQuantity - item.receivedQuantity;
        if (Math.abs(delta) < 0.001) return null;
        return {
          sourceType: item.sourceType,
          sourceId: item.sourceId,
          itemId: item.itemId,
          ingredientId: item.ingredientId,
          receivedQuantity: item.receivedQuantity,
          confirmedQuantity,
        };
      })
      .filter(Boolean) as any[];

    if (adjustments.length === 0) {
      toast.info('No hay ajustes para aplicar');
      return;
    }

    setApplying(true);
    try {
      const response = await discrepancyReviewService.applyAdjustments(filters.locationId, adjustments);
      if (response.success) {
        toast.success(response.message);
        setEditedQuantities({});
        await fetchDiscrepancies();
      } else {
        toast.error('Error al aplicar ajustes');
      }
    } catch (error) {
      console.error('Error applying adjustments:', error);
      toast.error('Error al aplicar ajustes');
    } finally {
      setApplying(false);
    }
  }, [editedQuantities, items, filters.locationId, fetchDiscrepancies]);

  const pendingAdjustmentsCount = Object.entries(editedQuantities).filter(([id, qty]) => {
    const item = items.find(i => i.id === id);
    return item && Math.abs(qty - item.receivedQuantity) > 0.001;
  }).length;

  const clearFilters = useCallback(() => {
    setFilters(prev => ({
      ...getDefaultFilters(),
      locationId: prev.locationId,
    }));
  }, []);

  return {
    items,
    summary,
    filters,
    editedQuantities,
    loading,
    applying,
    hasSearched,
    locations,
    categories,
    pendingAdjustmentsCount,
    updateFilters,
    fetchDiscrepancies,
    updateConfirmedQuantity,
    resetEdits,
    applyAdjustments,
    clearFilters,
  };
};
